import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LiveStreamPlayer from '../components/LiveStreamPlayer';
import { useCameraStream } from '../contexts/NodeStreamContext';
import { useNode } from '../contexts/NodeContext';
import {
  HeatmapOverlay,
  HeatmapPage as SharedHeatmapPage,
  type HeatmapPlayerArgs,
} from '@trinetra/shared';

/**
 * One camera's stream through the tunnel, with the heat layer stacked on it.
 *
 * Its own component because `useCameraStream` is a hook and the camera it subscribes to is
 * chosen inside the shared page — a hook can't be called from a render callback, but the
 * callback can return a component that calls it.
 */
function TunnelPlayer({ nodeId, cameraId, cameraName, overlay }: HeatmapPlayerArgs & { nodeId: string }) {
  // The session-level connection the camera detail page already uses, so opening this view
  // only adds a track rather than negotiating a second peer connection.
  const { stream, state, onStalled, onPlaying, onRetry } = useCameraStream(nodeId, cameraId);
  return (
    <LiveStreamPlayer
      stream={stream}
      label={cameraName}
      connectionState={state}
      className="h-full w-full rounded-none"
      onRetry={onRetry}
      onStalled={onStalled}
      onPlaying={onPlaying}
      videoOverlay={overlay ? <HeatmapOverlay {...overlay} /> : null}
    />
  );
}

/**
 * The heatmap, for one camera at a time, with every reporting camera on the rail beside it.
 *
 * Serves BOTH `/reports/heatmap` (no camera in the path — the page opens on the busiest one)
 * and `/nodes/:nodeId/cameras/:cameraId/heatmap` (the live view's own link). The two used to be
 * a list page and a view page; they are one page now, and the player above is the only thing
 * that differs between this and the local node's copy.
 */
export default function HeatmapPage() {
  const { nodeId: routeNodeId, cameraId: routeCameraId } = useParams<{ nodeId?: string; cameraId?: string }>();
  const { selectedNode } = useNode();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // The route's node wins where there is one — a pasted link names the site it was taken on.
  const nodeId = routeNodeId || selectedNode?.nodeId || '';
  const cameraId = routeCameraId || searchParams.get('camera') || undefined;

  return (
    <SharedHeatmapPage
      key={nodeId}
      nodeKey={nodeId}
      initialCameraId={cameraId}
      onCameraChange={(id, name) => {
        // Replace, not push: flicking down the rail is looking around one page, not a trail of
        // pages to walk back through.
        if (routeCameraId) {
          navigate(`/nodes/${nodeId}/cameras/${id}/heatmap?name=${encodeURIComponent(name || 'Camera')}`, { replace: true });
        } else {
          setSearchParams({ camera: id }, { replace: true });
        }
      }}
      renderPlayer={(args) => <TunnelPlayer key={args.cameraId} nodeId={nodeId} {...args} />}
      emptyState={
        nodeId ? undefined : <div className="p-6 text-muted-foreground">Select a site to view heatmaps.</div>
      }
    />
  );
}
